/**
 * World Cup Glass — Theme JS
 * Animations: GSAP ScrollTrigger + vanilla JS
 * Inspired by 21st.dev: container-scroll, hero-scroll, parallax concepts
 */

document.addEventListener('DOMContentLoaded', () => {
  initHeader();
  initHeroAnimations();
  initScrollReveal();
  initScrollTriggerAnimations();
  initTiltCards();
  initCountdown();
  initQuantitySelector();
  initStockMeter();
});

/* =============================================
   HEADER — scroll state
   ============================================= */
function initHeader() {
  const header = document.getElementById('site-header');
  if (!header) return;
  const onScroll = () => {
    header.classList.toggle('scrolled', window.scrollY > 50);
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
}

/* =============================================
   HERO — entrance animations
   ============================================= */
function initHeroAnimations() {
  const eyebrow = document.querySelector('.hero__eyebrow');
  const title = document.querySelector('.hero__title');
  const subtitle = document.querySelector('.hero__subtitle');
  const actions = document.querySelector('.hero__actions');
  const productFloat = document.querySelector('.hero__product-float');

  const items = [eyebrow, title, subtitle, actions, productFloat].filter(Boolean);

  items.forEach((el, i) => {
    setTimeout(() => {
      el.style.transition = 'opacity 0.8s cubic-bezier(0.16,1,0.3,1), transform 0.8s cubic-bezier(0.16,1,0.3,1)';
      el.style.opacity = '1';
      el.style.transform = el === productFloat ? 'translateX(0)' : 'translateY(0)';
    }, 200 + i * 140);
  });

  if (productFloat) {
    productFloat.style.transform = 'translateX(60px)';
  }
}

/* =============================================
   SCROLL REVEAL — IntersectionObserver
   ============================================= */
function initScrollReveal() {
  const els = document.querySelectorAll('.reveal');
  if (!els.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('in-view');
        observer.unobserve(entry.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -60px 0px' });

  els.forEach(el => observer.observe(el));
}

/* =============================================
   GSAP SCROLL TRIGGER ANIMATIONS
   ============================================= */
function initScrollTriggerAnimations() {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

  gsap.registerPlugin(ScrollTrigger);

  // 3D Container scroll — inspired by 21st.dev container-scroll-animation
  const scroll3dCard = document.querySelector('.scroll-3d-card');
  if (scroll3dCard) {
    gsap.fromTo(scroll3dCard, {
      rotateX: 20,
      scale: 0.82,
      opacity: 0.6,
    }, {
      rotateX: 0,
      scale: 1,
      opacity: 1,
      ease: 'power2.out',
      scrollTrigger: {
        trigger: '.scroll-3d-section',
        start: 'top 85%',
        end: 'top 20%',
        scrub: 1.5,
      }
    });
  }

  // Hero parallax
  const heroBg = document.querySelector('.hero__gradient');
  const heroPitchLines = document.querySelector('.hero__pitch-lines');
  if (heroBg) {
    gsap.to(heroBg, {
      yPercent: 30,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1,
      }
    });
  }
  if (heroPitchLines) {
    gsap.to(heroPitchLines, {
      yPercent: 50,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 2,
      }
    });
  }

  // Product float parallax
  const productFloat = document.querySelector('.hero__product-float');
  if (productFloat) {
    gsap.to(productFloat, {
      y: -80,
      ease: 'none',
      scrollTrigger: {
        trigger: '.hero',
        start: 'top top',
        end: 'bottom top',
        scrub: 1,
      }
    });
  }

  // Stats counter animation
  const statNumbers = document.querySelectorAll('.stat-number[data-count]');
  statNumbers.forEach(el => {
    const target = parseInt(el.dataset.count, 10);
    const suffix = el.dataset.suffix || '';
    const prefix = el.dataset.prefix || '';
    ScrollTrigger.create({
      trigger: el,
      start: 'top 85%',
      onEnter: () => animateCount(el, 0, target, 1600, prefix, suffix),
      once: true,
    });
  });

  // Feature cards stagger
  const featureCards = document.querySelectorAll('.feature-card');
  if (featureCards.length) {
    gsap.fromTo(featureCards, {
      opacity: 0,
      y: 50,
    }, {
      opacity: 1,
      y: 0,
      duration: 0.7,
      stagger: 0.12,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.features-grid',
        start: 'top 85%',
      }
    });
  }

  // Review cards stagger
  const reviewCards = document.querySelectorAll('.review-card');
  if (reviewCards.length) {
    gsap.fromTo(reviewCards, {
      opacity: 0,
      y: 40,
    }, {
      opacity: 1,
      y: 0,
      duration: 0.65,
      stagger: 0.1,
      ease: 'power3.out',
      scrollTrigger: {
        trigger: '.reviews-grid',
        start: 'top 85%',
      }
    });
  }
}

function animateCount(el, from, to, duration, prefix, suffix) {
  const start = performance.now();
  const update = (now) => {
    const elapsed = now - start;
    const progress = Math.min(elapsed / duration, 1);
    const ease = 1 - Math.pow(1 - progress, 4);
    const current = Math.round(from + (to - from) * ease);
    el.textContent = prefix + current.toLocaleString() + suffix;
    if (progress < 1) requestAnimationFrame(update);
  };
  requestAnimationFrame(update);
}

/* =============================================
   3D TILT EFFECT on product images
   ============================================= */
function initTiltCards() {
  const tiltEls = document.querySelectorAll('.tilt-card');

  tiltEls.forEach(card => {
    card.addEventListener('mousemove', (e) => {
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(600px) rotateX(${y * -12}deg) rotateY(${x * 12}deg) scale(1.02)`;
    });

    card.addEventListener('mouseleave', () => {
      card.style.transform = 'perspective(600px) rotateX(0deg) rotateY(0deg) scale(1)';
      card.style.transition = 'transform 0.5s cubic-bezier(0.16,1,0.3,1)';
    });

    card.addEventListener('mouseenter', () => {
      card.style.transition = 'transform 0.1s ease-out';
    });
  });
}

/* =============================================
   COUNTDOWN TIMER
   ============================================= */
function initCountdown() {
  const el = document.getElementById('countdown-timer');
  if (!el) return;

  // Target: end of World Cup 2026 (July 19, 2026)
  const target = new Date('2026-07-19T23:59:59').getTime();

  function update() {
    const now = Date.now();
    const diff = Math.max(0, target - now);
    const d = Math.floor(diff / 86400000);
    const h = Math.floor((diff % 86400000) / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);

    const fmt = n => String(n).padStart(2, '0');
    const dd = el.querySelector('[data-unit="days"]');
    const dh = el.querySelector('[data-unit="hours"]');
    const dm = el.querySelector('[data-unit="mins"]');
    const ds = el.querySelector('[data-unit="secs"]');

    if (dd) dd.textContent = fmt(d);
    if (dh) dh.textContent = fmt(h);
    if (dm) dm.textContent = fmt(m);
    if (ds) ds.textContent = fmt(s);
  }

  update();
  setInterval(update, 1000);
}

/* =============================================
   QUANTITY SELECTOR
   ============================================= */
function initQuantitySelector() {
  document.querySelectorAll('.qty-selector').forEach(selector => {
    const input = selector.querySelector('.qty-input');
    const dec = selector.querySelector('[data-qty="dec"]');
    const inc = selector.querySelector('[data-qty="inc"]');
    if (!input || !dec || !inc) return;

    dec.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      if (val > 1) input.value = val - 1;
    });
    inc.addEventListener('click', () => {
      const val = parseInt(input.value, 10);
      input.value = val + 1;
    });
  });
}

/* =============================================
   STOCK METER animation
   ============================================= */
function initStockMeter() {
  const fill = document.querySelector('.stock-fill');
  if (!fill) return;

  const observer = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting) {
      setTimeout(() => {
        fill.style.width = fill.dataset.fill || '23%';
      }, 300);
      observer.disconnect();
    }
  });
  observer.observe(fill);
}

/* =============================================
   SPARKS / CONFETTI on CTA click
   ============================================= */
document.querySelectorAll('.btn--primary, .btn--gold').forEach(btn => {
  btn.addEventListener('click', function(e) {
    spawnParticles(e.clientX, e.clientY);
  });
});

function spawnParticles(x, y) {
  const colors = ['#E8001D', '#FFD700', '#00A651', '#FFFFFF'];
  for (let i = 0; i < 16; i++) {
    const p = document.createElement('div');
    const angle = (Math.PI * 2 * i) / 16;
    const velocity = 80 + Math.random() * 80;
    const size = 4 + Math.random() * 6;

    Object.assign(p.style, {
      position: 'fixed',
      left: x + 'px',
      top: y + 'px',
      width: size + 'px',
      height: size + 'px',
      borderRadius: '50%',
      background: colors[Math.floor(Math.random() * colors.length)],
      pointerEvents: 'none',
      zIndex: 9998,
      transform: 'translate(-50%, -50%)',
    });

    document.body.appendChild(p);

    const tx = Math.cos(angle) * velocity;
    const ty = Math.sin(angle) * velocity;

    p.animate([
      { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
      { transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0)`, opacity: 0 }
    ], {
      duration: 600 + Math.random() * 400,
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)',
      fill: 'forwards'
    }).onfinish = () => p.remove();
  }
}
